// src/pages/Reuniones.jsx
import Cargando from "../components/Cargando";
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useReuniones, crearReunion } from "../hooks/useReuniones";
import { useClientes } from "../hooks/useClientes";
import { useAuth } from "../hooks/useAuth";
import { useRol }  from "../hooks/useRoles";
import { fechaHoyLocal } from "../utils/fechas";
import toast from "react-hot-toast";

// ── Constantes de opciones ────────────────────────────────────────────────────
const TIPOS = ["Visita presencial", "Videollamada", "Llamada", "WhatsApp", "Otro"];

const DOLORES_COMUNES = [
  "Demora en entrega", "Precio variable", "Falta de stock",
  "Mala calidad", "Soporte deficiente", "Logística compleja",
  "Poco crédito", "Proceso de cotización lento",
];
const OBJECIONES_COMUNES = [
  "Ya tiene proveedor", "Solo busca precio", "No hay presupuesto ahora",
  "Necesita aprobación interna", "Precio alto", "Plazo de entrega largo",
];
const PRIORIDADES_COMUNES = [
  "Rapidez de entrega", "Crédito disponible", "Precio competitivo",
  "Calidad del material", "Soporte técnico", "Garantía",
];

const COLOR_PROB = (p) => {
  if (p >= 70) return { bg: "#E1F5EE", color: "#0F6E56", border: "#1D9E75" };
  if (p >= 40) return { bg: "#FAEEDA", color: "#854F0B", border: "#D97706" };
  return { bg: "#FCEBEB", color: "#A32D2D", border: "#DC2626" };
};

const AV_COLORS = [
  { bg: "#EDE9FE", color: "#5B21B6" },
  { bg: "#E0F2FE", color: "#0369A1" },
  { bg: "#FEF3C7", color: "#92400E" },
  { bg: "#FCE7F3", color: "#9D174D" },
  { bg: "#D1FAE5", color: "#065F46" },
];
const avColor = (nombre) => AV_COLORS[(nombre?.charCodeAt(0) || 0) % AV_COLORS.length];

function fmtFecha(f) {
  if (!f) return "—";
  const [y, m, d] = f.split("-");
  return `${d}/${m}/${y}`;
}

// ── Formulario nueva reunión ──────────────────────────────────────────────────
const FORM_VACIO = {
  clienteId: "", clienteNombre: "", clienteEmpresa: "",
  responsable: "", fecha: fechaHoyLocal(), tipo: "Visita presencial",
  dolores: [], objeciones: [], prioridades: [],
  proximasCompras: [], probabilidadCierre: 50, notas: "", proximoContacto: "",
};

function ToggleChip({ label, activo, onClick, color = "#7C3AED" }) {
  return (
    <button type="button" onClick={onClick} style={{
      fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 20,
      cursor: "pointer", border: `1.5px solid ${activo ? color : "#e2e8f0"}`,
      background: activo ? color : "#fff", color: activo ? "#fff" : "#64748b",
      transition: "background 120ms cubic-bezier(0.23,1,0.32,1), border-color 120ms, color 120ms",
    }}>{label}</button>
  );
}

function FormReunion({ clientes, onGuardar, onCancelar, guardando, initialForm, editId, usuarioActual, esAdmin, asesores }) {
  const [form, setForm] = useState(() => ({
    ...(initialForm || FORM_VACIO),
    responsable: initialForm?.responsable || usuarioActual || "",
  }));
  const [compraActual, setCompraActual] = useState({ material: "", cantidad: "", fechaEstimada: "" });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleArr = (key, val) =>
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
    }));

  const selCliente = (id) => {
    const cli = clientes.find(c => c.id === id);
    setForm(f => ({
      ...f,
      clienteId:      cli?.id        || "",
      clienteNombre:  cli?.nombre    || "",
      clienteEmpresa: cli?.empresa   || "",
      responsable:    cli?.responsable || f.responsable,
    }));
  };

  const agregarCompra = () => {
    if (!compraActual.material.trim()) return;
    setForm(f => ({ ...f, proximasCompras: [...f.proximasCompras, { ...compraActual }] }));
    setCompraActual({ material: "", cantidad: "", fechaEstimada: "" });
  };

  const quitarCompra = (i) =>
    setForm(f => ({ ...f, proximasCompras: f.proximasCompras.filter((_, idx) => idx !== i) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.clienteId) { toast.error("Selecciona un cliente"); return; }
    onGuardar(form);
  };

  const input = { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "inherit" };
  const label = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 };
  const sec = { marginBottom: 18 };

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 14, padding: "22px 24px", marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 18 }}>Nueva reunión</h3>
      <form onSubmit={handleSubmit}>

        {/* Fila 1: cliente + fecha + tipo */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={label}>Cliente <span style={{ color: "#DC2626" }}>*</span></label>
            {editId ? (
              <div style={{ ...input, background: "#f8fafc", color: "#475569", display: "flex", alignItems: "center" }}>
                {form.clienteNombre}{form.clienteEmpresa ? ` · ${form.clienteEmpresa}` : ""}
              </div>
            ) : (
              <select value={form.clienteId} onChange={e => selCliente(e.target.value)}
                style={{ ...input, color: form.clienteId ? "#1e293b" : "#94a3b8" }} required>
                <option value="">Seleccionar cliente...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}{c.empresa ? ` · ${c.empresa}` : ""}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label style={label}>Fecha</label>
            <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)} style={input} />
          </div>
          <div>
            <label style={label}>Tipo</label>
            <select value={form.tipo} onChange={e => set("tipo", e.target.value)} style={input}>
              {TIPOS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Responsable */}
        <div style={{ marginBottom: 16 }}>
          <label style={label}>
            Responsable
            <span style={{ fontSize: 10, fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
              {esAdmin ? "admin: puedes cambiar el asesor" : "asesor que realiza la reunión"}
            </span>
          </label>
          {esAdmin && asesores?.length > 0 ? (
            <select value={form.responsable} onChange={e => set("responsable", e.target.value)} style={input}>
              {asesores.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          ) : (
            <div style={{ ...input, background: "#f8fafc", color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, background: "#EDE9FE", color: "#7C3AED", padding: "1px 7px", borderRadius: 5, fontWeight: 600 }}>Tú</span>
              {form.responsable || usuarioActual}
            </div>
          )}
        </div>

        {/* Dolores detectados */}
        <div style={sec}>
          <label style={label}>Dolores detectados</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {DOLORES_COMUNES.map(d => (
              <ToggleChip key={d} label={d} activo={form.dolores.includes(d)}
                onClick={() => toggleArr("dolores", d)} color="#DC2626" />
            ))}
          </div>
          <input placeholder="Otro dolor (Enter para agregar)..."
            style={{ ...input, fontSize: 12 }}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (e.target.value.trim()) { toggleArr("dolores", e.target.value.trim()); e.target.value = ""; } } }}
          />
          {form.dolores.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {form.dolores.map(d => (
                <span key={d} style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 10, background: "#FCEBEB", color: "#A32D2D", display: "flex", alignItems: "center", gap: 4 }}>
                  {d} <span style={{ cursor: "pointer", fontWeight: 700 }} onClick={() => toggleArr("dolores", d)}>×</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Objeciones */}
        <div style={sec}>
          <label style={label}>Objeciones</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {OBJECIONES_COMUNES.map(o => (
              <ToggleChip key={o} label={o} activo={form.objeciones.includes(o)}
                onClick={() => toggleArr("objeciones", o)} color="#D97706" />
            ))}
          </div>
          {form.objeciones.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {form.objeciones.map(o => (
                <span key={o} style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 10, background: "#FAEEDA", color: "#854F0B", display: "flex", alignItems: "center", gap: 4 }}>
                  {o} <span style={{ cursor: "pointer", fontWeight: 700 }} onClick={() => toggleArr("objeciones", o)}>×</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Prioridades */}
        <div style={sec}>
          <label style={label}>Prioridades del cliente</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {PRIORIDADES_COMUNES.map(p => (
              <ToggleChip key={p} label={p} activo={form.prioridades.includes(p)}
                onClick={() => toggleArr("prioridades", p)} color="#1D9E75" />
            ))}
          </div>
        </div>

        {/* Próximas compras */}
        <div style={sec}>
          <label style={label}>Próximas compras detectadas</label>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, marginBottom: 8 }}>
            <input placeholder="Material (ej: Cemento Portland)"
              value={compraActual.material} onChange={e => setCompraActual(c => ({ ...c, material: e.target.value }))}
              style={input} />
            <input placeholder="Cantidad"
              value={compraActual.cantidad} onChange={e => setCompraActual(c => ({ ...c, cantidad: e.target.value }))}
              style={input} />
            <input type="date" value={compraActual.fechaEstimada}
              onChange={e => setCompraActual(c => ({ ...c, fechaEstimada: e.target.value }))}
              style={input} />
            <button type="button" onClick={agregarCompra} style={{
              padding: "8px 14px", background: "#EDE9FE", color: "#7C3AED",
              border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>+ Agregar</button>
          </div>
          {form.proximasCompras.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {form.proximasCompras.map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#F8FAFC", borderRadius: 8, padding: "6px 12px", fontSize: 12 }}>
                  <span style={{ flex: 1, fontWeight: 600, color: "#1e293b" }}>📦 {c.material}</span>
                  {c.cantidad && <span style={{ color: "#64748b" }}>{c.cantidad}</span>}
                  {c.fechaEstimada && <span style={{ color: "#64748b" }}>→ {fmtFecha(c.fechaEstimada)}</span>}
                  <span onClick={() => quitarCompra(i)} style={{ cursor: "pointer", color: "#DC2626", fontWeight: 700 }}>×</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Probabilidad + próximo contacto + notas */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={label}>Probabilidad de cierre: <strong style={{ color: COLOR_PROB(form.probabilidadCierre).border }}>{form.probabilidadCierre}%</strong></label>
            <input type="range" min={0} max={100} step={5}
              value={form.probabilidadCierre} onChange={e => set("probabilidadCierre", Number(e.target.value))}
              style={{ width: "100%", accentColor: COLOR_PROB(form.probabilidadCierre).border }} />
          </div>
          <div>
            <label style={label}>Próximo contacto</label>
            <input type="date" value={form.proximoContacto} min={fechaHoyLocal()}
              onChange={e => set("proximoContacto", e.target.value)} style={input} />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={label}>Notas adicionales</label>
          <textarea value={form.notas} onChange={e => set("notas", e.target.value)}
            placeholder="¿Qué más pasó en la reunión? Contexto, acuerdos, compromisos..."
            rows={3} style={{ ...input, resize: "vertical" }} />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={guardando} style={{
            padding: "10px 24px", background: "#7C3AED", color: "#fff",
            border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: guardando ? "not-allowed" : "pointer", opacity: guardando ? 0.7 : 1,
          }}>
            {guardando ? "Guardando..." : editId ? "✓ Actualizar reunión" : "✓ Guardar reunión"}
          </button>
          <button type="button" onClick={onCancelar} style={{
            padding: "10px 16px", background: "#f1f5f9", color: "#475569",
            border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer",
          }}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

// ── Card de reunión ───────────────────────────────────────────────────────────
function ReunionCard({ r, onEditar, onEliminar }) {
  const [confirmando, setConfirmando] = useState(false);
  const prob   = r.probabilidadCierre || 0;
  const probSt = COLOR_PROB(prob);
  const av     = avColor(r.clienteNombre);

  return (
    <div className="reunion-card" style={{
      background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12,
      padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 14,
    }}>
      {/* Avatar */}
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
        {(r.clienteNombre || "?").charAt(0).toUpperCase()}
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <Link to={`/clientes/${r.clienteId}/perfil`} style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", textDecoration: "none" }}>
            {r.clienteNombre}
          </Link>
          {r.clienteEmpresa && <span style={{ fontSize: 11, color: "#94a3b8" }}>{r.clienteEmpresa}</span>}
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "#EDE9FE", color: "#7C3AED" }}>
            {r.tipo || "Reunión"}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: probSt.bg, color: probSt.color }}>
            {prob}% cierre
          </span>
        </div>

        {/* Dolores */}
        {r.dolores?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 5 }}>
            {r.dolores.map(d => (
              <span key={d} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D" }}>⚡ {d}</span>
            ))}
          </div>
        )}

        {/* Objeciones */}
        {r.objeciones?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 5 }}>
            {r.objeciones.map(o => (
              <span key={o} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: "#FAEEDA", color: "#854F0B" }}>🛡 {o}</span>
            ))}
          </div>
        )}

        {/* Próximas compras */}
        {r.proximasCompras?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 5 }}>
            {r.proximasCompras.map((c, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8, background: "#E1F5EE", color: "#0F6E56" }}>
                📦 {c.material}{c.cantidad ? ` · ${c.cantidad}` : ""}{c.fechaEstimada ? ` → ${fmtFecha(c.fechaEstimada)}` : ""}
              </span>
            ))}
          </div>
        )}

        {r.notas && <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 3 }}>📝 {r.notas}</div>}
      </div>

      {/* Fecha, responsable y acciones */}
      <div style={{ textAlign: "right", flexShrink: 0, minWidth: 90, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{fmtFecha(r.fecha)}</div>
        {r.responsable && <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.responsable.split(" ")[0]}</div>}
        {r.proximoContacto && (
          <div style={{ fontSize: 10, fontWeight: 600, color: "#7C3AED" }}>→ {fmtFecha(r.proximoContacto)}</div>
        )}
        <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
          <button onClick={() => onEditar(r)} style={{
            fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 6,
            background: "#EEF2FF", color: "#3A3C7A", border: "none", cursor: "pointer",
          }}>Editar</button>
          {confirmando ? (
            <>
              <button onClick={() => { onEliminar(r.id); setConfirmando(false); }} style={{
                fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6,
                background: "#DC2626", color: "#fff", border: "none", cursor: "pointer",
              }}>Sí</button>
              <button onClick={() => setConfirmando(false)} style={{
                fontSize: 10, padding: "3px 8px", borderRadius: 6,
                background: "#f1f5f9", color: "#64748b", border: "none", cursor: "pointer",
              }}>No</button>
            </>
          ) : (
            <button onClick={() => setConfirmando(true)} style={{
              fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 6,
              background: "#FEE2E2", color: "#DC2626", border: "none", cursor: "pointer",
            }}>×</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Reuniones() {
  const { reuniones, loading, actualizar, eliminar } = useReuniones();
  const { clientes }           = useClientes();
  const { user }               = useAuth();
  const { perfil, esAdmin }    = useRol();

  const usuarioActual = perfil?.nombre || user?.email || "";

  const asesores = useMemo(() => {
    const set = new Set(reuniones.map(r => r.responsable).filter(Boolean));
    if (usuarioActual) set.add(usuarioActual);
    return [...set].sort();
  }, [reuniones, usuarioActual]);

  const [mostrarForm,   setMostrarForm]   = useState(false);
  const [editandoId,    setEditandoId]    = useState(null);
  const [guardando,     setGuardando]     = useState(false);
  const [busqueda,      setBusqueda]      = useState("");
  const [filtroTipo,    setFiltroTipo]    = useState("todos");
  const [filtroProb,    setFiltroProb]    = useState("todos");

  // KPI chips
  const kpis = useMemo(() => {
    const hace7 = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
    return {
      total:       reuniones.length,
      estaSemana:  reuniones.filter(r => r.fecha >= hace7).length,
      sinFollowUp: reuniones.filter(r => !r.proximoContacto).length,
      altaProb:    reuniones.filter(r => (r.probabilidadCierre || 0) >= 70).length,
      probProm:    reuniones.length > 0
        ? Math.round(reuniones.reduce((s, r) => s + (r.probabilidadCierre || 0), 0) / reuniones.length)
        : 0,
    };
  }, [reuniones]);

  const filtradas = useMemo(() => {
    let lista = [...reuniones];
    if (busqueda) {
      const b = busqueda.toLowerCase();
      lista = lista.filter(r =>
        r.clienteNombre?.toLowerCase().includes(b) ||
        r.clienteEmpresa?.toLowerCase().includes(b) ||
        r.notas?.toLowerCase().includes(b) ||
        r.dolores?.some(d => d.toLowerCase().includes(b)) ||
        r.responsable?.toLowerCase().includes(b)
      );
    }
    if (filtroTipo !== "todos") lista = lista.filter(r => r.tipo === filtroTipo);
    if (filtroProb === "alta")  lista = lista.filter(r => (r.probabilidadCierre || 0) >= 70);
    if (filtroProb === "media") lista = lista.filter(r => (r.probabilidadCierre || 0) >= 40 && (r.probabilidadCierre || 0) < 70);
    if (filtroProb === "baja")  lista = lista.filter(r => (r.probabilidadCierre || 0) < 40);
    if (filtroProb === "sin-followup") lista = lista.filter(r => !r.proximoContacto);
    return lista;
  }, [reuniones, busqueda, filtroTipo, filtroProb]);

  const abrirEditar = (r) => {
    setEditandoId(r.id);
    setMostrarForm(true);
  };

  const cerrarForm = () => {
    setMostrarForm(false);
    setEditandoId(null);
  };

  const guardar = async (form) => {
    setGuardando(true);
    try {
      if (editandoId) {
        await actualizar(editandoId, {
          dolores:            form.dolores,
          objeciones:         form.objeciones,
          prioridades:        form.prioridades,
          proximasCompras:    form.proximasCompras,
          probabilidadCierre: form.probabilidadCierre,
          notas:              form.notas,
          proximoContacto:    form.proximoContacto,
          tipo:               form.tipo,
          fecha:              form.fecha,
        });
        toast.success("✅ Reunión actualizada");
      } else {
        await crearReunion(form, user?.uid);
        toast.success("✅ Reunión registrada");
      }
      cerrarForm();
    } catch {
      toast.error("Error al guardar la reunión");
    }
    setGuardando(false);
  };

  if (loading) return <Cargando />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <style>{`
        .reunion-card {
          transition: box-shadow 200ms cubic-bezier(0.23,1,0.32,1),
                      transform 200ms cubic-bezier(0.23,1,0.32,1);
        }
        .reunion-card:hover {
          box-shadow: 0 4px 16px rgba(124,58,237,0.10);
          transform: translateY(-1px);
        }
        .reu-chip {
          transition: background 150ms cubic-bezier(0.23,1,0.32,1),
                      box-shadow 150ms cubic-bezier(0.23,1,0.32,1),
                      transform 100ms cubic-bezier(0.23,1,0.32,1);
        }
        .reu-chip:active { transform: scale(0.96) !important; }
        .reu-chip:hover  { filter: brightness(0.95); }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>Reuniones</h1>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {filtradas.length} reunión{filtradas.length !== 1 ? "es" : ""} · Prob. promedio: <strong style={{ color: "#7C3AED" }}>{kpis.probProm}%</strong>
          </div>
        </div>
        <button onClick={() => { if (mostrarForm) cerrarForm(); else setMostrarForm(true); }} style={{
          background: mostrarForm ? "#f1f5f9" : "#7C3AED", color: mostrarForm ? "#475569" : "#fff",
          border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13,
          fontWeight: 600, cursor: "pointer",
          transition: "background 150ms cubic-bezier(0.23,1,0.32,1)",
        }}>
          {mostrarForm ? "Cancelar" : "+ Nueva reunión"}
        </button>
      </div>

      {/* Formulario */}
      {mostrarForm && (
        <FormReunion
          key={editandoId || "nuevo"}
          clientes={clientes}
          onGuardar={guardar}
          onCancelar={cerrarForm}
          guardando={guardando}
          editId={editandoId}
          initialForm={editandoId ? reuniones.find(r => r.id === editandoId) : FORM_VACIO}
          usuarioActual={usuarioActual}
          esAdmin={esAdmin}
          asesores={asesores}
        />
      )}

      {/* KPI chips */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        {[
          { key: "todos",       label: "Todas",        count: kpis.total,       sub: `prom. ${kpis.probProm}%`,    color: "#7C3AED" },
          { key: "esta-semana", label: "Esta semana",  count: kpis.estaSemana,  sub: "últimos 7 días",             color: "#1D9E75" },
          { key: "alta",        label: "Alta prob.",   count: kpis.altaProb,    sub: "≥ 70% cierre",               color: "#1E3A5F" },
          { key: "sin-followup",label: "Sin follow-up",count: kpis.sinFollowUp, sub: "sin próx. contacto",         color: "#DC2626" },
        ].map(f => {
          const activo = filtroProb === f.key || (f.key === "todos" && filtroProb === "todos" && filtroTipo === "todos");
          return (
            <button key={f.key} className="reu-chip"
              onClick={() => { setFiltroProb(f.key === "todos" ? "todos" : f.key); }}
              style={{
                background: activo ? f.color : "#fff", color: activo ? "#fff" : f.color,
                border: `1.5px solid ${activo ? f.color : "#e2e8f0"}`, borderRadius: 12,
                padding: "12px 8px", cursor: "pointer", textAlign: "center",
                boxShadow: activo ? `0 2px 10px ${f.color}44` : "none",
              }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3, opacity: activo ? 0.9 : 0.7 }}>{f.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{f.count}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: activo ? 0.9 : 0.7 }}>{f.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Filtros secundarios */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Buscar cliente, empresa, dolor, asesor..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, outline: "none" }}
        />
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#475569", background: "#fff" }}>
          <option value="todos">Tipo: Todos</option>
          {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(busqueda || filtroTipo !== "todos" || filtroProb !== "todos") && (
          <button onClick={() => { setBusqueda(""); setFiltroTipo("todos"); setFiltroProb("todos"); }}
            style={{ padding: "6px 12px", borderRadius: 8, fontSize: 11, color: "#DC2626", background: "#FCEBEB", border: "none", cursor: "pointer", fontWeight: 600 }}>
            × Limpiar
          </button>
        )}
      </div>

      {/* Lista */}
      {filtradas.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", background: "#fff", borderRadius: 12, border: "0.5px solid #e2e8f0", color: "#94a3b8", fontSize: 13 }}>
          {reuniones.length === 0
            ? "Sin reuniones registradas. Registra la primera con el botón de arriba."
            : "No hay reuniones con este filtro."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtradas.map((r, i) => (
            <div key={r.id} className="stagger-item" style={{ animationDelay: `${Math.min(i * 30, 240)}ms` }}>
              <ReunionCard r={r} onEditar={abrirEditar} onEliminar={eliminar} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
